import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ChatSidebar from '../chat/ChatSidebar';
import { StreamingChatProvider } from '../../contexts/StreamingChatContext';

const STORAGE_KEY = 'bizguard_chat_sidebar';
const MOBILE_BREAKPOINT = 768;

interface ChatLayoutContextValue {
  sidebarExpanded: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
}

const ChatLayoutContext = createContext<ChatLayoutContextValue>({
  sidebarExpanded: false,
  isMobile: false,
  toggleSidebar: () => {},
  closeSidebar: () => {},
});

export const useChatLayout = (): ChatLayoutContextValue => useContext(ChatLayoutContext);

interface ChatLayoutProps {
  children: React.ReactNode;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
      return false;
    }
    try {
      return localStorage.getItem(STORAGE_KEY) === 'expanded';
    } catch {
      return false;
    }
  });

  // Track viewport width for mobile detection
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent): void => {
      setIsMobile(e.matches);
      // Auto-close sidebar when switching to mobile
      if (e.matches) {
        setSidebarExpanded(false);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      if (!isMobile) {
        try {
          localStorage.setItem(STORAGE_KEY, next ? 'expanded' : 'collapsed');
        } catch {
          // localStorage may be unavailable (private mode, quota); preference resets next load.
        }
      }
      return next;
    });
  }, [isMobile]);

  const closeSidebar = useCallback(() => {
    setSidebarExpanded(false);
    if (!isMobile) {
      try {
        localStorage.setItem(STORAGE_KEY, 'collapsed');
      } catch {
        // localStorage may be unavailable (private mode, quota); preference resets next load.
      }
    }
  }, [isMobile]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const mod = e.metaKey || e.ctrlKey;
      // Ctrl/Cmd+B: toggle sidebar
      if (mod && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  return (
    <ChatLayoutContext.Provider value={{ sidebarExpanded, isMobile, toggleSidebar, closeSidebar }}>
      <StreamingChatProvider>
        <div className="flex min-h-screen bg-background text-foreground">
          <ChatSidebar />
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </StreamingChatProvider>
    </ChatLayoutContext.Provider>
  );
};

export default ChatLayout;
